import React, { useState, useEffect } from 'react';
import { JobApplication, ApplicationStatus } from '../types/application';
import ApplicationColumn from '../components/applications/ApplicationColumn';
import ApplicationStats from '../components/applications/ApplicationStats';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { jobsApi } from '../services/api/jobs';
import { useAuth } from '../contexts/AuthContext';
import InterviewBoard from '../components/interviews/InterviewBoard';
import { interviewApi } from '../services/api/interview';
import InterviewPipeline from '../components/interviews/InterviewPipeline';
import InterviewNotesSidebar from '../components/interviews/InterviewNotesSidebar';
import { Interview, InterviewStage } from '../types/interview';
import { InterviewStageName } from '../types/interview';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

interface ApplicationStats {
  total: number;
  toApply: number;
  applied: number;
  archived: number;
}

const BoardPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ApplicationStats>({
    total: 0,
    toApply: 0,
    applied: 0,
    archived: 0
  });
  const { user } = useAuth();
  const [showInterviewBoard, setShowInterviewBoard] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);

  useEffect(() => {
    loadApplications();
  }, []);

  useEffect(() => {
    setStats({
      total: applications.length,
      toApply: applications.filter(app => app.status === ApplicationStatus.TO_APPLY).length,
      applied: applications.filter(app => app.status === ApplicationStatus.APPLIED).length,
      archived: applications.filter(app => app.status === ApplicationStatus.ARCHIVED).length
    });
  }, [applications]);

  useEffect(() => {
    console.log('All applications:', applications);
    console.log('Interviewing applications:', applications.filter(app => 
      app.status === ApplicationStatus.INTERVIEWING
    ));
  }, [applications]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await jobsApi.getApplications(user?.id || '');
      setApplications(response.applications);
    } catch (err) {
      console.error('Failed to load applications:', err);
      setError('Failed to load your applications');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (jobId: number) => {
    navigate(`/jobs/${jobId}`, {
      state: { backgroundLocation: location }
    });
  };

  const handleCloseJobDetails = () => {
    navigate('/applications');
  };

  const handleStatusChange = async (applicationId: number, newStatus: ApplicationStatus) => {
    try {
      // Get the job ID from the current application
      const currentApp = applications.find(app => app.id === applicationId);
      if (!currentApp) {
        throw new Error('Application not found');
      }

      console.log(`Updating application ${applicationId} status to ${newStatus}`);
      await jobsApi.updateApplicationStatus(applicationId, user?.id || '', newStatus, currentApp.job_id);
      
      // Update local state
      setApplications(prevApplications =>
        prevApplications.map(app =>
          app.id === applicationId ? { ...app, status: newStatus } : app
        )
      );

      // // If status is changed to ACCEPTED or OFFER, ensure it's reflected in the interview stages
      // if (newStatus === ApplicationStatus.ACCEPTED || newStatus === ApplicationStatus.OFFER) {
      //   // Optionally refresh interview stages if needed
      //   const updatedStages = await interviewApi.getInterviews(applicationId, currentApp.job_id);
      //   // Handle the updated stages if needed
      // }
    } catch (err) {
      console.error('Failed to update application status:', err);
    }
  };

  const handleMoveToInterview = async (applicationId: number) => {
    try {
      console.log('Moving to interview:', applicationId);
      await interviewApi.createInterview(applicationId, 'screening');
      
      // Update local state to reflect the status change
      setApplications(prevApplications => {
        const updatedApplications = prevApplications.map(app =>
          app.id === applicationId
            ? { ...app, status: ApplicationStatus.INTERVIEWING }
            : app
        );
        console.log('Updated applications:', updatedApplications);
        return updatedApplications;
      });
    } catch (error) {
      console.error('Failed to create interview:', error);
    }
  };

  const handleOpenNotes = (interview: Interview, stage: InterviewStage) => {
    console.log('Opening notes for interview:', interview, 'stage:', stage);
    const interviewWithJobId = {
      ...interview,
      job_id: stage.job_id
    };
    setSelectedInterview(interviewWithJobId);
  };

  const handleCloseNotes = () => {
    setSelectedInterview(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner message="Loading your applications..." className="text-blue-600" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadApplications} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 pt-28">
      <div className="container mx-auto px-4 pb-20">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          {/* Title and Icon */}
          <div className="flex flex-col items-center justify-center space-y-6 mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-xl" />
              <div className="relative bg-gradient-to-br from-blue-500 to-purple-600 p-4 rounded-2xl shadow-lg">
                <ClipboardDocumentListIcon className="h-10 w-10 text-white" />
              </div>
            </motion.div>
            
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent text-center">
              My Applications
            </h1>
          </div>

          {/* Stats Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            {[
              { label: 'Total Applications', value: stats.total, color: 'from-blue-500 to-blue-600' },
              { label: 'To Apply', value: stats.toApply, color: 'from-purple-500 to-purple-600' },
              { label: 'Applied', value: stats.applied, color: 'from-green-500 to-green-600' },
              { label: 'Archived', value: stats.archived, color: 'from-gray-500 to-gray-600' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`text-3xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                  {stat.value}
                </div>
                <div className="text-gray-600 text-sm mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
        
        {/* Application Columns */}
        <motion.div 
          className="flex gap-8 overflow-x-auto pb-8 px-2 snap-x snap-mandatory"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex gap-8 min-w-full">
            {[
              {
                status: ApplicationStatus.TO_APPLY,
                title: "To Apply",
                applications: applications.filter(app => app.status === ApplicationStatus.TO_APPLY),
                index: 0
              },
              {
                status: ApplicationStatus.APPLIED,
                title: "Applied",
                applications: applications.filter(app => app.status === ApplicationStatus.APPLIED),
                index: 1
              },
              {
                status: ApplicationStatus.ARCHIVED,
                title: "Archived",
                applications: applications.filter(app => app.status === ApplicationStatus.ARCHIVED),
                index: 2
              }
            ].map((column) => (
              <motion.div
                key={column.status}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + column.index * 0.1 }}
                className="flex-1 snap-start"
              >
                <ApplicationColumn
                  status={column.status}
                  title={column.title}
                  applications={column.applications}
                  onCardClick={handleCardClick}
                  onStatusChange={handleStatusChange}
                  onMoveToInterview={handleMoveToInterview}
                  index={column.index}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Interview Pipeline Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-12 bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6"
        >
          <InterviewPipeline
            applications={applications.filter(app => app.status === ApplicationStatus.INTERVIEWING)}
            interview={selectedInterview}
            onStatusChange={handleStatusChange}
            onOpenNotes={handleOpenNotes}
          />
        </motion.div>
      </div>

      <Outlet context={{ onClose: handleCloseJobDetails }} />

      <InterviewNotesSidebar
        isOpen={!!selectedInterview}
        onClose={handleCloseNotes}
        interview={selectedInterview}
        currentStage={selectedInterview?.stage as InterviewStageName || 'screening'}
        onStageComplete={async (id: number) => { 
          console.log("Stage complete", id); 
          return; 
        }}
      />
    </div>
  );
};

export default BoardPage;